/**
 * PasteImagePlugin - Handles pasting images from external sources like Microsoft Word.
 *
 * Performance strategy (two-phase paste):
 *   Phase 1 (instant, synchronous):
 *     - Create blob URLs via URL.createObjectURL() for each clipboard image
 *     - Replace broken file:/// sources in the HTML with blob URLs
 *     - Process the corrected HTML through Lexical immediately
 *     - User sees text AND images on screen with zero perceptible delay
 *
 *   Phase 2 (background, async):
 *     - Compress / resize large images via canvas (Word images are often huge)
 *     - Convert compressed images to base64 data URIs
 *     - Walk the Lexical tree and swap blob URLs → base64 on each InlineImageNode
 *     - We do NOT revoke blob URLs: Lexical's undo stack may still hold editor
 *       states that reference them. Revoking would make Undo show a broken image
 *       (alt text + placeholder) when restoring a state that used the blob.
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateNodesFromDOM } from '@lexical/html';
import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_HIGH,
  $addUpdateTag,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  HISTORY_MERGE_TAG,
  HISTORY_PUSH_TAG,
  $createParagraphNode,
  $isRootOrShadowRoot,
} from 'lexical';
import { useEffect } from 'react';

import { INSERT_INLINE_IMAGE_COMMAND } from '../InlineImagePlugin';
import { $createInlineImageNode } from '../../nodes/InlineImageNode/InlineImageNode';
import { $wrapNodeInElement } from '@lexical/utils';
import { trimTablesToMaxColumns, MAX_TABLE_COLUMNS } from '../../utils/tablePasteUtils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MAX_IMAGE_WIDTH = 1600; // px – resize anything wider than this
const COMPRESSION_QUALITY = 0.85; // JPEG quality for compressed images

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Read a File as a base64 data-URI string (fallback, un-compressed). */
function readFileAsBase64(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/**
 * Compress / resize an image via an off-screen canvas.
 * - Skips tiny files (< 50 KB).
 * - Skips images already within MAX_IMAGE_WIDTH and < 500 KB.
 * - Uses JPEG for photos, PNG when transparency matters.
 * Returns a base64 data-URI string.
 */
function compressImage(file) {
  return new Promise((resolve) => {
    // For non-image or very small files, just read directly
    if (!file.type.startsWith('image/') || file.size < 50_000) {
      readFileAsBase64(file).then(resolve);
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(blobUrl);

      // If already small enough, skip the canvas round-trip
      if (img.width <= MAX_IMAGE_WIDTH && file.size < 500_000) {
        readFileAsBase64(file).then(resolve);
        return;
      }

      try {
        const canvas = document.createElement('canvas');
        let w = img.width;
        let h = img.height;

        if (w > MAX_IMAGE_WIDTH) {
          const ratio = MAX_IMAGE_WIDTH / w;
          w = MAX_IMAGE_WIDTH;
          h = Math.round(h * ratio);
        }

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        // Use PNG only when the source is PNG (to keep transparency)
        const outType =
          file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        resolve(canvas.toDataURL(outType, COMPRESSION_QUALITY));
      } catch (err) {
        readFileAsBase64(file).then(resolve);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      readFileAsBase64(file).then(resolve);
    };

    img.src = blobUrl;
  });
}

/** True when the src cannot be loaded by a browser (file:///, empty, etc.). */
function isBrokenImageSrc(src) {
  if (!src || src.trim() === '') return true;
  if (src.startsWith('data:')) return false;
  if (src.startsWith('http://') || src.startsWith('https://')) return false;
  // file:///, blob: from another origin, or any other non-web protocol
  return true;
}

/**
 * Parse a CSS length value (e.g. "6.268in", "451.5pt", "602px") to pixels.
 * Returns an integer or null.
 */
function parseCSSLengthToPixels(value) {
  if (!value || value === 'auto' || value === 'inherit') return null;
  const num = parseFloat(value);
  if (isNaN(num) || num <= 0) return null;
  if (value.endsWith('in')) return Math.round(num * 96);
  if (value.endsWith('pt')) return Math.round(num * (96 / 72));
  if (value.endsWith('cm')) return Math.round(num * (96 / 2.54));
  if (value.endsWith('mm')) return Math.round(num * (96 / 25.4));
  if (value.endsWith('px')) return Math.round(num);
  if (value.endsWith('%') || value.endsWith('em') || value.endsWith('rem')) return null;
  if (!isNaN(Number(value))) return Math.round(num);
  return null;
}

/**
 * Normalize dimension attributes on an <img> element so that
 * $convertInlineImageElement always has pixel-based width/height.
 * Word often sets dimensions only via inline style (e.g. "width:6.268in").
 */
function normalizeImageDimensions(img) {
  let w = parseInt(img.getAttribute('width'), 10) || 0;
  let h = parseInt(img.getAttribute('height'), 10) || 0;

  // Try inline style if attribute is missing
  if (!w && img.style && img.style.width) {
    w = parseCSSLengthToPixels(img.style.width) || 0;
  }
  if (!h && img.style && img.style.height) {
    h = parseCSSLengthToPixels(img.style.height) || 0;
  }

  // Apply a sensible max-width for pasted images (editor is typically ~800-1200px)
  const MAX_PASTE_WIDTH = 800;
  if (w > MAX_PASTE_WIDTH) {
    if (h) h = Math.round(h * (MAX_PASTE_WIDTH / w));
    w = MAX_PASTE_WIDTH;
  }

  // Set the normalized values as HTML attributes so Lexical picks them up
  if (w) img.setAttribute('width', String(w));
  if (h) img.setAttribute('height', String(h));

  // Remove Word's inline width/height styles to prevent conflicts
  if (img.style) {
    img.style.removeProperty('width');
    img.style.removeProperty('height');
  }
}

/** Synchronously extract image File objects from clipboard items. */
function extractImageFiles(clipboardData) {
  const files = [];
  const items = clipboardData.items;
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
  }
  // Fallback: clipboardData.files
  if (files.length === 0 && clipboardData.files) {
    for (let i = 0; i < clipboardData.files.length; i++) {
      const f = clipboardData.files[i];
      if (f.type.startsWith('image/')) files.push(f);
    }
  }
  return files;
}

/**
 * Walk the Lexical node tree depth-first and replace every blob-URL src
 * on InlineImageNode (or ImageNode) with its permanent base64 equivalent.
 */
function replaceBlobUrlsInTree(node, blobToBase64) {
  const type = node.__type;
  if (
    (type === 'inline-image' || type === 'image') &&
    node.__src &&
    node.__src.startsWith('blob:')
  ) {
    const base64 = blobToBase64.get(node.__src);
    if (base64) {
      const writable = node.getWritable();
      writable.__src = base64;
    }
  }
  // Recurse into children (ParagraphNode, etc.)
  if (typeof node.getChildren === 'function') {
    node.getChildren().forEach((child) => replaceBlobUrlsInTree(child, blobToBase64));
  }
}

// ---------------------------------------------------------------------------
// Plugin
// ---------------------------------------------------------------------------
export default function PasteImagePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboardData = event?.clipboardData;
        if (!clipboardData) return false;

        const imageFiles = extractImageFiles(clipboardData);
        const htmlString = clipboardData.getData('text/html');

        // When Word pastes text+images, it often embeds images as base64 in the HTML
        // rather than in clipboardData.items. We must handle HTML-with-images ourselves
        // even when imageFiles is empty, or the default handler may drop the images.
        const hasHtmlWithImages =
          htmlString && /<img[\s>]/i.test(htmlString);

        if (imageFiles.length === 0 && !hasHtmlWithImages) {
          return false;
        }

        // Prevent browser default paste when we handle it - avoids double-insert and ensures history works
        if (event?.preventDefault) event.preventDefault();

        // =============================================================
        // CASE 1 – HTML present (typical Word paste: text + images)
        // =============================================================
        if (htmlString) {
          const parser = new DOMParser();
          const dom = parser.parseFromString(htmlString, 'text/html');
          const allImgs = Array.from(dom.querySelectorAll('img'));

          const broken = allImgs.filter((img) =>
            isBrokenImageSrc(img.getAttribute('src') || '')
          );

          // All images valid (e.g. base64 in Word) – insert ourselves to ensure
          // they are preserved; default Lexical paste can drop them.
          if (broken.length === 0 && allImgs.length > 0) {
            allImgs.forEach(normalizeImageDimensions);
            const trimmedCount = trimTablesToMaxColumns(dom);
            if (trimmedCount > 0) {
              setTimeout(() => alert(`Tables are limited to ${MAX_TABLE_COLUMNS} columns. Excess columns were removed.`), 100);
            }
            editor.update(() => {
              $addUpdateTag(HISTORY_PUSH_TAG);
              try {
                const nodes = $generateNodesFromDOM(editor, dom);
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                  selection.insertNodes(nodes);
                } else {
                  $insertNodes(nodes);
                }
              } catch (err) {
                console.error('[PasteImagePlugin] insert error:', err);
              }
            });
            requestAnimationFrame(() => editor.getRootElement()?.focus());
            return true;
          }

          if (broken.length > 0) {
            // ── Phase 1 – instant blob-URL display ──────────────────
            const blobUrls = []; // track for later revocation
            const blobToFileIdx = new Map();

            broken.forEach((img, idx) => {
              if (idx < imageFiles.length) {
                const blobUrl = URL.createObjectURL(imageFiles[idx]);
                img.setAttribute('src', blobUrl);
                if (!img.getAttribute('alt')) {
                  img.setAttribute('alt', imageFiles[idx]?.name || 'Pasted image');
                }
                // Normalize dimensions – convert Word's CSS units to pixels
                // and cap at a reasonable max width
                normalizeImageDimensions(img);
                blobUrls.push(blobUrl);
                blobToFileIdx.set(blobUrl, idx);
              } else {
                img.remove(); // no corresponding file – discard
              }
            });

            const trimmedCount = trimTablesToMaxColumns(dom);
            if (trimmedCount > 0) {
              setTimeout(() => alert(`Tables are limited to ${MAX_TABLE_COLUMNS} columns. Excess columns were removed.`), 100);
            }
            // Insert immediately – the user sees text + images right away
            editor.update(() => {
              $addUpdateTag(HISTORY_PUSH_TAG);
              try {
                const nodes = $generateNodesFromDOM(editor, dom);
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                  selection.insertNodes(nodes);
                } else {
                  $insertNodes(nodes);
                }
              } catch (err) {
                console.error('[PasteImagePlugin] insert error:', err);
              }
            });
            // Force view to update so pasted images show immediately (no keypress needed)
            requestAnimationFrame(() => {
              editor.getRootElement()?.focus();
            });

            // ── Phase 2 – background: compress + swap to base64 ─────
            const filesToConvert = imageFiles.slice(0, broken.length);
            Promise.all(filesToConvert.map(compressImage))
              .then((base64Results) => {
                const blobToBase64 = new Map();
                blobUrls.forEach((url, i) => {
                  if (base64Results[i]) blobToBase64.set(url, base64Results[i]);
                });

                editor.update(() => {
                  $addUpdateTag(HISTORY_MERGE_TAG); // Merge with paste so one undo removes the whole paste
                  try {
                    replaceBlobUrlsInTree($getRoot(), blobToBase64);
                  } catch (err) {
                    console.error('[PasteImagePlugin] base64 swap error:', err);
                  }
                });

                // Do not revoke blob URLs here; undo stack may restore a state
                // that still references them, which would show broken image + alt text.
              })
              .catch((err) => {
                console.error('[PasteImagePlugin] compression error:', err);
              });

            return true; // handled
          }

          // HTML has no <img> tags but clipboard has image files (e.g. Excel table + snapshot)
          if (allImgs.length === 0) {
            const trimmedCount = trimTablesToMaxColumns(dom);
            if (trimmedCount > 0) {
              setTimeout(() => alert(`Tables are limited to ${MAX_TABLE_COLUMNS} columns. Excess columns were removed.`), 100);
            }
            // Insert HTML (table or other content) immediately
            editor.update(() => {
              $addUpdateTag(HISTORY_PUSH_TAG);
              try {
                const nodes = $generateNodesFromDOM(editor, dom);
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                  selection.insertNodes(nodes);
                } else {
                  $insertNodes(nodes);
                }
              } catch (err) {
                console.error('[PasteImagePlugin] text insert error:', err);
              }
            });

            // When pasting a table (e.g. from Excel), the clipboard often also contains
            // an image (Excel's selection snapshot). Do not insert that as a separate
            // image — only the table/content was intended. See Lexical playground behavior.
            const hasTable = dom.querySelector('table') !== null;
            if (hasTable) {
              return true;
            }

            // No table: treat clipboard images as intended paste (e.g. screenshot + HTML)
            Promise.all(imageFiles.map(compressImage))
              .then((base64Results) => {
                base64Results.forEach((b64, i) => {
                  if (b64) {
                    editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, {
                      altText: imageFiles[i]?.name || 'Pasted image',
                      src: b64,
                      position: 'left',
                      showCaption: false,
                    });
                  }
                });
              })
              .catch((err) => {
                console.error('[PasteImagePlugin] image insert error:', err);
              });

            return true;
          }
        }

        // =============================================================
        // CASE 2 – No HTML, just raw image files (screenshot paste etc.)
        // =============================================================
        if (!htmlString) {
          // Phase 1: instant blob URL insert — use editor.update() directly with
          // HISTORY_PUSH_TAG so the push lands in a single top-level update.
          // Dispatching INSERT_INLINE_IMAGE_COMMAND here would cause a nested
          // editor.update() inside the PASTE_COMMAND handler; Lexical batches
          // nested updates and the HISTORY_PUSH_TAG gets lost, so undo never
          // activates on the first paste.
          const blobUrls = imageFiles.map((file) => URL.createObjectURL(file));

          editor.update(() => {
            $addUpdateTag(HISTORY_PUSH_TAG);
            blobUrls.forEach((blobUrl, i) => {
              try {
                const imageNode = $createInlineImageNode({
                  altText: imageFiles[i]?.name || 'Pasted image',
                  src: blobUrl,
                  position: 'left',
                  showCaption: false,
                });
                $insertNodes([imageNode]);
                if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
                  $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
                }
              } catch (err) {
                console.error('[PasteImagePlugin] blob insert error:', err);
              }
            });
          });

          requestAnimationFrame(() => editor.getRootElement()?.focus());

          // Phase 2: background compress + swap
          Promise.all(imageFiles.map(compressImage))
            .then((base64Results) => {
              editor.update(() => {
                $addUpdateTag(HISTORY_MERGE_TAG); // Merge with paste so one undo removes the whole paste
                try {
                  const blobToBase64 = new Map();
                  // We need to match – use the root tree scan
                  const root = $getRoot();
                  // Collect all blob-src nodes
                  const blobNodes = [];
                  (function walk(n) {
                    if (
                      (n.__type === 'inline-image' || n.__type === 'image') &&
                      n.__src &&
                      n.__src.startsWith('blob:')
                    ) {
                      blobNodes.push(n);
                    }
                    if (typeof n.getChildren === 'function') {
                      n.getChildren().forEach(walk);
                    }
                  })(root);

                  blobNodes.forEach((node, i) => {
                    if (base64Results[i]) {
                      const writable = node.getWritable();
                      writable.__src = base64Results[i];
                      // Do not revoke the old blob URL; undo may restore it.
                    }
                  });
                } catch (err) {
                  console.error('[PasteImagePlugin] raw swap error:', err);
                }
              });
            })
            .catch((err) => {
              console.error('[PasteImagePlugin] raw compress error:', err);
            });

          return true;
        }

        return false; // let default Lexical handler run
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
}
